import { singleton } from 'aurelia';
import { FetchMode } from '../models';

// Owns the diagnostics log shown in the admin panel: the running list of entries, the
// append/scroll behavior, and the bounded history. The log element ref is set by the
// admin-log component so auto-scroll can pin to the newest entry.
@singleton()
export class DiagnosticsService {
  diagnostics: string[] = [];
  logContent: HTMLElement | undefined;

  private static readonly MaxLogEntries = 500;

  private static readonly months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  clear(): void {
    this.diagnostics = [];
  }

  // Appends a raw block of lines (used for ad-hoc admin notices like "Retry unknowns").
  append(lines: string[]): void {
    const wasAtBottom = this.isLogAtBottom();
    this.diagnostics = [...this.diagnostics, ...lines];
    this.capHistory();
    if (wasAtBottom) {
      this.scrollLogToBottom();
    }
  }

  // Appends a labeled block for a release fetch so appended history stays readable.
  appendFetch(mode: FetchMode, year: number, month: number, entries: string[]): void {
    const time = new Date().toLocaleTimeString();
    const label = mode === 'month' ? 'Month' : 'Load';
    const header = `\u2500\u2500 ${label} \u00b7 ${DiagnosticsService.months[month - 1]} ${year} \u00b7 ${time} \u2500\u2500`;

    // Only auto-scroll if the user is already pinned to the bottom, so appending does
    // not yank them away from earlier history they may be reading.
    const wasAtBottom = this.isLogAtBottom();

    const block = [header, ...entries];
    // Append to the running log rather than replacing it, keeping prior months' output.
    this.diagnostics = [...this.diagnostics, ...block];
    this.capHistory();

    if (wasAtBottom) {
      this.scrollLogToBottom();
    }
  }

  private capHistory(): void {
    // Cap the log so it cannot grow without bound over a long session.
    if (this.diagnostics.length > DiagnosticsService.MaxLogEntries) {
      this.diagnostics = this.diagnostics.slice(this.diagnostics.length - DiagnosticsService.MaxLogEntries);
    }
  }

  private isLogAtBottom(): boolean {
    const el = this.logContent;
    if (!el) return true; // Not yet rendered (e.g. initial load): default to pinning.
    // A small threshold absorbs sub-pixel rounding in scroll measurements.
    return el.scrollTop + el.clientHeight >= el.scrollHeight - 4;
  }

  scrollLogToBottom(): void {
    // Defer until after Aurelia has flushed the new entries into the DOM so
    // scrollHeight reflects the appended content.
    requestAnimationFrame(() => {
      if (this.logContent) {
        this.logContent.scrollTop = this.logContent.scrollHeight;
      }
    });
  }
}
